import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    labels: Object,
  };

  async show(event) {
    event.preventDefault();

    const form = this._form();
    if (!form || !this._validatedEntries(form)) return;

    this._renderConfirmation(form);
  }

  hide(event) {
    event?.preventDefault();

    const form = this._form();
    if (!form) return;

    this.element.querySelector('#confirmationView')?.remove();
    form.style.display = 'block';
  }

  async confirm(event) {
    event.preventDefault();

    const form = this._form();
    if (!form) return;

    if (this.element.querySelector('#confirmationView')) this.hide();

    const entries = this._validatedEntries(form, { syncCheckboxes: true });
    if (!entries) return;

    const { data, imagesToProcess } = entries;

    try {
      for (const { key, file } of imagesToProcess) {
        data[key] = await this._readFileAsBase64(file);
      }
    } catch (error) {
      console.error('[summary] failed to read image', error);
      alert(this.labelsValue.image_read_error);
      return;
    }

    data.timestamp = new Date().toISOString();
    this._submitStoredData(data);
  }

  _validatedEntries(form, { syncCheckboxes = false } = {}) {
    if (syncCheckboxes) this._syncCheckboxGroups(form);

    const entries = this._collectFormEntries(form);
    if (typeof window.validateFormData === 'function' && !window.validateFormData(entries.data)) {
      return null;
    }

    return entries;
  }

  _collectFormEntries(form) {
    const data = {};
    const imagesToProcess = [];
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      if (value instanceof File && value.size > 0) {
        imagesToProcess.push({ key, file: value });
      } else {
        data[key] = value;
      }
    }

    return { data, imagesToProcess };
  }

  _syncCheckboxGroups(form) {
    const syncGroup = (key) => {
      const checkedValues = [];

      form.querySelectorAll(`input[type="checkbox"][name="${key}_dummy"]:checked`).forEach((cb) => {
        if (cb.id === `${key}_other`) return;
        checkedValues.push(cb.value);
      });

      const otherField = form.querySelector(`input[type="text"][name="other_${key}_input"]`);
      if (otherField && otherField.value.trim() !== '') {
        checkedValues.push(otherField.value.trim());
      }

      const hiddenField = form.querySelector(`input#${key}`);
      if (hiddenField) hiddenField.value = checkedValues.join(', ');
    };

    new Set(
      Array.from(form.querySelectorAll('input[type="checkbox"][name$="_dummy"]')).map((cb) =>
        cb.name.replace(/_dummy$/, '')
      )
    ).forEach(syncGroup);
  }

  _submitStoredData(data) {
    if (!navigator.onLine) {
      alert(this.labelsValue.offline_alert);
      return;
    }

    document.dispatchEvent(new CustomEvent('dynform:submit', { detail: { submission: data } }));
  }

  _form() {
    return this.element.querySelector('#myForm');
  }

  _readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.error) {
          reject(reader.error);
        } else {
          resolve(reader.result);
        }
      };
      reader.readAsDataURL(file);
    });
  }

  _renderConfirmation(form) {
    const formCopy = form.cloneNode(true);
    // Keep confirmation copy inert to avoid booting a second Stimulus instance.
    formCopy.removeAttribute('data-controller');
    formCopy.removeAttribute('data-action');

    const title = document.createElement('h2');
    title.textContent = this.labelsValue.please_review;
    title.className = 'text-2xl font-bold mb-4 text-left mt-2';
    formCopy.insertBefore(title, formCopy.firstChild);

    formCopy.querySelectorAll('.input-prefix, .input-suffix').forEach((el) => el.remove());

    const checkboxGroups = {};
    formCopy.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      const groupName = checkbox.name;
      checkboxGroups[groupName] ||= [];

      const label = checkbox.parentNode.querySelector('span');
      if (checkbox.checked && label) {
        if (!checkbox.id || !checkbox.id.endsWith('_other')) {
          checkboxGroups[groupName].push(label.textContent.trim());
        }
      }
    });

    Object.entries(checkboxGroups).forEach(([groupName, selectedOptions]) => {
      if (selectedOptions.length === 0) return;

      const otherInputName = `other_${groupName.replace('_dummy', '')}_input`;
      const otherInput = formCopy.querySelector(`input[name="${otherInputName}"]`);
      if (otherInput && otherInput.value.trim() !== '') {
        selectedOptions.push(otherInput.value.trim());
      }

      const checkbox = formCopy.querySelector(`input[name="${groupName}"]`);
      if (checkbox) {
        const containerDiv = checkbox.closest('div');
        const summary = document.createElement('div');
        summary.className = 'my-2';
        summary.textContent = selectedOptions.join(', ') || this.labelsValue.none;
        containerDiv.parentNode.replaceChild(summary, containerDiv);

        if (otherInput) {
          const otherContainer = otherInput.closest('div');
          if (otherContainer) otherContainer.remove();
          else otherInput.remove();
        }
      }
    });

    const originalSignatureFields = form.querySelectorAll('.signature-field');
    const clonedSignatureFields = formCopy.querySelectorAll('.signature-field');

    clonedSignatureFields.forEach((clonedField, index) => {
      const originalField = originalSignatureFields[index];
      if (!originalField) return;

      const originalInput = originalField.querySelector('[data-signature-pad-target="input"]');
      const originalCanvas = originalField.querySelector('[data-signature-pad-target="canvas"]');

      let signatureBase64 = originalInput?.value || '';
      if (!signatureBase64 && originalCanvas) {
        signatureBase64 = originalCanvas.toDataURL('image/png');
      }

      const signatureArea = clonedField.querySelector(
        '.border.border-gray-400.rounded-md.bg-gray-100.p-2'
      );
      if (!signatureArea) return;

      if (!signatureBase64) {
        signatureArea.remove();
        return;
      }

      const signatureImage = document.createElement('img');
      signatureImage.src = signatureBase64;
      signatureImage.alt = this.labelsValue.signature;
      signatureImage.className = 'mt-2 rounded border max-w-full h-auto';
      signatureArea.parentNode.replaceChild(signatureImage, signatureArea);
    });

    const inputs = formCopy.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]), select'
    );

    inputs.forEach((input) => {
      let value = input.value;
      const label = input.id ? formCopy.querySelector(`label[for="${input.id}"]`) : null;
      if (!label) return;

      const labelText = label.textContent;
      label.remove();

      if (input.tagName.toLowerCase() === 'select') {
        const originalSelect = document.getElementById(input.id);
        const selectedOption = originalSelect?.options?.[originalSelect.selectedIndex];
        value = selectedOption ? selectedOption.text : this.labelsValue.not_selected;
      }

      const wrapper = document.createElement('div');
      const strong = document.createElement('strong');
      strong.textContent = labelText;
      wrapper.appendChild(strong);
      wrapper.appendChild(document.createTextNode(`: ${value}`));
      input.parentNode.replaceChild(wrapper, input);
    });

    formCopy
      .querySelectorAll(
        'input[type="hidden"],input[type="submit"],input[type="button"]#dynamicFormSubmit,input[type="checkbox"],.dropzone'
      )
      .forEach((el) => el.remove());

    const buttonWrapper = document.createElement('div');
    buttonWrapper.className = 'mt-4 flex justify-between';

    const cancelButton = document.createElement('button');
    cancelButton.textContent = this.labelsValue.cancel;
    cancelButton.className =
      'py-2 px-4 font-bold uppercase text-grey-100 hover:text-gray bg-gray-200 hover:bg-gray-300 rounded-lg';
    cancelButton.id = 'confirmCancelButton';
    cancelButton.type = 'button';
    cancelButton.setAttribute('data-action', 'click->dynform--summary-overlay#hide');

    const submitButton = document.createElement('button');
    submitButton.textContent = this.labelsValue.submit;
    submitButton.className =
      'py-2 px-4 font-bold uppercase text-grey-1 hover:text-white hover:bg-primary/70 bg-gradient-to-r hover:animate-pulse whitespace-nowrap from-shade-3 to-shade-2 rounded-lg';
    submitButton.id = 'confirmSubmitButton';
    submitButton.type = 'button';
    submitButton.setAttribute('data-action', 'click->dynform--summary-overlay#confirm');

    buttonWrapper.appendChild(cancelButton);
    buttonWrapper.appendChild(submitButton);
    formCopy.appendChild(buttonWrapper);

    const confirmationView = document.createElement('div');
    confirmationView.id = 'confirmationView';
    confirmationView.className = formCopy.className;
    confirmationView.appendChild(formCopy);

    form.style.display = 'none';
    this.element.appendChild(confirmationView);
  }
}
